import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'innermatelic.sidebar.collapsed';
const SYNC_EVENT = 'innermatelic:sidebar';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

function write(next: boolean) {
  window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  window.dispatchEvent(new CustomEvent<boolean>(SYNC_EVENT, { detail: next }));
}

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(readInitial);

  useEffect(() => {
    const onSync = (e: Event) => {
      const next = (e as CustomEvent<boolean>).detail;
      setCollapsed(typeof next === 'boolean' ? next : readInitial());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setCollapsed(e.newValue === '1');
    };
    window.addEventListener(SYNC_EVENT, onSync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, onSync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ⌘\ / Ctrl+\ 로 사이드바 토글. 입력 중에도 동작.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.code === 'Backslash') {
        e.preventDefault();
        const next = !readInitial();
        write(next);
        setCollapsed(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggle = useCallback(() => {
    const next = !readInitial();
    write(next);
    setCollapsed(next);
  }, []);

  const open = useCallback(() => {
    write(false);
    setCollapsed(false);
  }, []);

  return { collapsed, toggle, open };
}
